import { useCallback, useEffect, useState } from 'react'

import { useContentSpaceRestCache, useRestResource } from './ContentSpaceRestContext'
import {
  createCourseGroup,
  createCoursePortal,
  getCourse,
  getCoursePortalsForCourse,
  getCourseGroups,
  getCourses,
  getCourseSessions,
  getCourseSessionsAnalytics,
  getCustomDomainStatus,
  getIntegrations,
  getAllPortals,
  getPerCoursePortal,
  getPortals,
  getPublicPortalCourse,
  getSsoConfigs,
  moveCourseToGroup,
  setCoursePortals,
  updateCourseStatus,
  updatePortalStatus,
  uploadCourse,
  getCourseVersions,
  activateCourseVersion,
} from './api'
import type { ICourseSessionsAnalytics } from './api'
import type {
  ContentSpacePublishStatus,
  ContentSpaceSessionStatus,
  ContentSpaceSessionType,
  IContentSpaceIntegration,
  IContentSpaceSession,
  ICoursePortal,
  ICourseGroup,
  ICourseVersion,
  ICustomDomainStatus,
  IGetCoursesParams,
  IGetPortalsParams,
  ILmsCourse,
  IPortalSsoConfig,
} from './types'

// Stable serialization for cache-key normalization (ADR-0001 §Decision).
// Exported so Task 1.5 (ContentSpaceRestContext) can reuse it.
export const serializeParams = (params: Record<string, unknown> | undefined): string => {
  if (!params) {
    return ''
  }
  const keys = Object.keys(params).sort()
  const entries: string[] = []
  for (const key of keys) {
    const value = params[key]
    if (value === undefined || value === null) {
      continue
    }
    if (Array.isArray(value)) {
      if (value.length === 0) {
        continue
      }
      entries.push(`${key}=${[...value].sort().join(',')}`)
      continue
    }
    entries.push(`${key}=${String(value)}`)
  }
  return entries.join('&')
}

// Cache-key helpers — keep in one place so Task 1.5 / invalidate() can import them.
export const makeCacheKey = {
  courses: (companyId: string, params?: IGetCoursesParams) =>
    `courses:${companyId}:${serializeParams(params as Record<string, unknown> | undefined)}`,
  portals: (companyId: string, params?: IGetPortalsParams) =>
    `portals:${companyId}:${serializeParams(params as Record<string, unknown> | undefined)}`,
  course: (companyId: string, courseId: string) => `course:${companyId}:${courseId}`,
  portal: (companyId: string, portalId: string) => `portal:${companyId}:${portalId}`,
  coursePortals: (companyId: string, courseId: string) => `coursePortals:${companyId}:${courseId}`,
  providers: (companyId: string, portalId: string) => `providers:${companyId}:${portalId}`,
  customDomain: (companyId: string, portalId: string) => `customDomain:${companyId}:${portalId}`,
  integrations: (companyId: string, portalId: string) => `integrations:${companyId}:${portalId}`,
}

// All list/entity hooks below use the shared ContentSpaceRestContext cache
// (ADR-0001, Task 1.5): dedupe by cacheKey, invalidation via invalidate(prefix).
type IRestHookResult<T> = ReturnType<typeof useRestResource<T>>

// ========== COURSES ==========

export interface IUseCoursesResult extends IRestHookResult<ILmsCourse[]> {
  total: number
  page: number
  pageSize: number
}

export const useCourses = (companyId?: string, params?: IGetCoursesParams): IUseCoursesResult => {
  const cacheKey = companyId ? makeCacheKey.courses(companyId, params) : null
  const fetcher = useCallback(
    () => getCourses(companyId as string, params),
    // cacheKey captures all inputs
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cacheKey],
  )
  const { data, ...rest } = useRestResource(cacheKey, fetcher)
  return {
    ...rest,
    data: data ? data.data : null,
    total: data?.total ?? 0,
    page: data?.page ?? 1,
    pageSize: data?.pageSize ?? 0,
  }
}

export const useCourse = (courseId?: string, companyId?: string) => {
  const cacheKey = courseId && companyId ? makeCacheKey.course(companyId, courseId) : null
  const fetcher = useCallback(
    () => getCourse(courseId as string, companyId as string),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cacheKey],
  )
  return useRestResource(cacheKey, fetcher)
}

// ========== PORTALS ==========

export interface IUsePortalsResult extends IRestHookResult<ICoursePortal[]> {
  total: number
  page: number
  pageSize: number
}

export const usePortals = (companyId?: string, params?: IGetPortalsParams): IUsePortalsResult => {
  const cacheKey = companyId ? makeCacheKey.portals(companyId, params) : null
  const fetcher = useCallback(
    () => getPortals(companyId as string, params),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cacheKey],
  )
  const { data, ...rest } = useRestResource(cacheKey, fetcher)
  return {
    ...rest,
    data: data ? data.data : null,
    total: data?.total ?? 0,
    page: data?.page ?? 1,
    pageSize: data?.pageSize ?? 0,
  }
}

// Fetches the complete, unpaginated portals list for a company.
// Use this when you need the full set (filters, lookups by id, name display).
// For paginated UI, use `usePortals`.
export const useAllPortals = (companyId?: string) => {
  const cacheKey = companyId ? `portals:all:${companyId}` : null
  const fetcher = useCallback(
    () => getAllPortals(companyId as string),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cacheKey],
  )
  return useRestResource<ICoursePortal[]>(cacheKey, fetcher)
}

export const usePortal = (companyId?: string, portalId?: string) => {
  // Backend exposes portal data only via listPortals — filter client-side for now.
  // Dedicated GET /portals/:portalId is a follow-up (backend-contracts §2).
  const cacheKey = companyId && portalId ? makeCacheKey.portal(companyId, portalId) : null
  const fetcher = useCallback(async () => {
    const data = await getAllPortals(companyId as string)
    return data.find((p) => p.id === portalId) ?? null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey])
  return useRestResource<ICoursePortal | null>(cacheKey, fetcher)
}

// ========== COURSE ↔ PORTAL (N:M) ==========

export const useCoursePortalsForCourse = (companyId?: string, courseId?: string) => {
  const cacheKey = companyId && courseId ? makeCacheKey.coursePortals(companyId, courseId) : null
  const fetcher = useCallback(
    () => getCoursePortalsForCourse(companyId as string, courseId as string),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cacheKey],
  )
  return useRestResource<string[]>(cacheKey, fetcher)
}

export const usePortalCourses = (
  companyId?: string,
  portalId?: string,
  params?: Omit<IGetCoursesParams, 'portalId'>,
): IUseCoursesResult => {
  const effectiveParams: IGetCoursesParams | undefined = portalId
    ? { ...(params ?? {}), portalId }
    : undefined
  return useCourses(portalId ? companyId : undefined, effectiveParams)
}

// ========== PORTAL SSO / Custom domain / Integrations ==========

export interface IUseProvidersArgs {
  portalId: string
}

export const useProviders = (companyId?: string, args?: IUseProvidersArgs) => {
  const portalId = args?.portalId
  const cacheKey = companyId && portalId ? makeCacheKey.providers(companyId, portalId) : null
  const fetcher = useCallback(async () => {
    const { configs } = await getSsoConfigs(companyId as string, portalId as string)
    return configs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey])
  return useRestResource<IPortalSsoConfig[]>(cacheKey, fetcher)
}

export const useCustomDomain = (companyId?: string, portalId?: string) => {
  const cacheKey = companyId && portalId ? makeCacheKey.customDomain(companyId, portalId) : null
  const fetcher = useCallback(
    () => getCustomDomainStatus(companyId as string, portalId as string),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cacheKey],
  )
  return useRestResource<ICustomDomainStatus>(cacheKey, fetcher)
}

export const useIntegrations = (companyId?: string, portalId?: string) => {
  const cacheKey = companyId && portalId ? makeCacheKey.integrations(companyId, portalId) : null
  const fetcher = useCallback(
    () => getIntegrations(companyId as string, portalId as string),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cacheKey],
  )
  return useRestResource<IContentSpaceIntegration[]>(cacheKey, fetcher)
}

// ========== LEGACY / UNCHANGED HOOKS ==========

export const useUploadCourse = () => {
  const cache = useContentSpaceRestCache()
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const upload = async (courseId: string, file: File, companyId: string) => {
    setUploading(true)
    setError(null)
    try {
      await uploadCourse(courseId, file, companyId)
      cache.invalidate(`courses:${companyId}`)
      cache.invalidate(`course:${companyId}:${courseId}`)
    } catch (err) {
      setError(err as Error)
      throw err
    } finally {
      setUploading(false)
    }
  }

  return { uploadCourse: upload, uploading, error }
}

export const useCourseGroups = (companyId?: string) => {
  const cacheKey = companyId ? `courseGroups:${companyId}` : null
  const fetcher = useCallback(
    () => getCourseGroups(companyId as string),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cacheKey],
  )
  return useRestResource<ICourseGroup[]>(cacheKey, fetcher)
}

export const useCreateCourseGroup = () => {
  const cache = useContentSpaceRestCache()
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const create = async (name: string, description: string | undefined, companyId: string) => {
    setCreating(true)
    setError(null)
    try {
      const result = await createCourseGroup(name, description, companyId)
      cache.invalidate(`courseGroups:${companyId}`)
      return result
    } catch (err) {
      setError(err as Error)
      throw err
    } finally {
      setCreating(false)
    }
  }

  return { createCourseGroup: create, creating, error }
}

export const useMoveCourse = () => {
  const cache = useContentSpaceRestCache()
  const [moving, setMoving] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const move = async (courseId: string, groupId: string | null, companyId: string) => {
    setMoving(true)
    setError(null)
    try {
      const result = await moveCourseToGroup(courseId, groupId, companyId)
      cache.invalidate(`courses:${companyId}`)
      cache.invalidate(`course:${companyId}:${courseId}`)
      return result
    } catch (err) {
      setError(err as Error)
      throw err
    } finally {
      setMoving(false)
    }
  }

  return { moveCourse: move, moving, error }
}

export const usePublicPortalCourse = (companyId: string, portalId: string, courseId: string) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [data, setData] = useState<any | null>(null) // TODO: Proper type
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const refetch = useCallback(() => {
    if (!courseId || !companyId || !portalId) {
      return
    }
    setLoading(true)
    getPublicPortalCourse(companyId, portalId, courseId)
      .then((res) => {
        setData(res)
        setLoading(false)
      })
      .catch((err) => {
        setError(err)
        setLoading(false)
      })
  }, [courseId, companyId, portalId])

  useEffect(() => {
    refetch()
  }, [refetch])

  return { data, loading, error, refetch }
}

export const useCourseSessions = (companyId?: string, courseId?: string, limit?: number) => {
  const [data, setData] = useState<IContentSpaceSession[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const refetch = useCallback(() => {
    if (!companyId || !courseId) {
      return
    }
    setLoading(true)
    getCourseSessions(companyId, courseId, { limit })
      .then(({ sessions }) => {
        setData(sessions)
        setLoading(false)
      })
      .catch((err) => {
        const errorObj =
          err instanceof Error
            ? err
            : new Error(
                (err as { message?: string } | null)?.message ?? String(err ?? 'Unknown error'),
              )
        setError(errorObj)
        setLoading(false)
      })
  }, [companyId, courseId, limit])

  const removeSession = useCallback((sessionId: string) => {
    setData((prev) => prev.filter((s) => s.id !== sessionId))
  }, [])

  useEffect(() => {
    refetch()
  }, [refetch])

  return { data, loading, error, refetch, removeSession }
}

export const useCourseSessionsAnalytics = (companyId?: string, courseId?: string) => {
  const [data, setData] = useState<ICourseSessionsAnalytics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const refetch = useCallback(() => {
    if (!companyId || !courseId) {
      return
    }
    setLoading(true)
    getCourseSessionsAnalytics(companyId, courseId)
      .then((analytics) => {
        setData(analytics)
        setLoading(false)
      })
      .catch((err) => {
        const errorObj =
          err instanceof Error
            ? err
            : new Error(
                (err as { message?: string } | null)?.message ?? String(err ?? 'Unknown error'),
              )
        setError(errorObj)
        setLoading(false)
      })
  }, [companyId, courseId])

  useEffect(() => {
    refetch()
  }, [refetch])

  return { data, loading, error, refetch }
}

export interface IUseCourseSessionsListParams {
  type?: ContentSpaceSessionType
  status?: ContentSpaceSessionStatus
  search?: string
  limit: number
  offset: number
}

export const useCourseSessionsList = (
  companyId: string | undefined,
  courseId: string | undefined,
  params: IUseCourseSessionsListParams,
) => {
  const [data, setData] = useState<IContentSpaceSession[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const { type, status, search, limit, offset } = params

  const refetch = useCallback(() => {
    if (!companyId || !courseId) {
      return
    }
    setLoading(true)
    getCourseSessions(companyId, courseId, { type, status, search, limit, offset })
      .then((res) => {
        setData(res.sessions)
        setTotal(res.total)
        setLoading(false)
      })
      .catch((err) => {
        const errorObj =
          err instanceof Error
            ? err
            : new Error(
                (err as { message?: string } | null)?.message ?? String(err ?? 'Unknown error'),
              )
        setError(errorObj)
        setLoading(false)
      })
  }, [companyId, courseId, type, status, search, limit, offset])

  const removeSession = useCallback((sessionId: string) => {
    setData((prev) => prev.filter((s) => s.id !== sessionId))
    setTotal((prev) => Math.max(0, prev - 1))
  }, [])

  useEffect(() => {
    refetch()
  }, [refetch])

  return { data, total, loading, error, refetch, removeSession }
}

export const useCreateCoursePortal = () => {
  const cache = useContentSpaceRestCache()
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const create = async (name: string, description: string | undefined, companyId: string) => {
    setCreating(true)
    setError(null)
    try {
      const result = await createCoursePortal(name, description, companyId)
      cache.invalidate(`portals:${companyId}`)
      cache.invalidate(`portals:all:${companyId}`)
      return result
    } catch (err) {
      setError(err as Error)
      throw err
    } finally {
      setCreating(false)
    }
  }

  return { createCoursePortal: create, creating, error }
}

export const useSetCoursePortals = () => {
  const cache = useContentSpaceRestCache()
  const [updating, setUpdating] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const setPortals = async (courseId: string, portalIds: string[], companyId: string) => {
    setUpdating(true)
    setError(null)
    try {
      const result = await setCoursePortals(courseId, portalIds, companyId)
      cache.invalidate(`coursePortals:${companyId}:${courseId}`)
      cache.invalidate(`portals:${companyId}`)
      cache.invalidate(`portals:all:${companyId}`)
      cache.invalidate(`courses:${companyId}`)
      return result
    } catch (err) {
      setError(err as Error)
      throw err
    } finally {
      setUpdating(false)
    }
  }

  return { setCoursePortals: setPortals, updating, error }
}

export const useUpdatePortalStatus = (companyId: string) => {
  const cache = useContentSpaceRestCache()
  const [updating, setUpdating] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const update = useCallback(
    async (portalId: string, status: ContentSpacePublishStatus) => {
      setUpdating(true)
      setError(null)
      try {
        const result = await updatePortalStatus(companyId, portalId, status)
        cache.invalidate(`portals:${companyId}`)
        cache.invalidate(`portals:all:${companyId}`)
        cache.invalidate(`portal:${companyId}:${portalId}`)
        return result
      } catch (err) {
        setError(err as Error)
        throw err
      } finally {
        setUpdating(false)
      }
    },
    [cache, companyId],
  )

  return { updatePortalStatus: update, updating, error }
}

export const useUpdateCourseStatus = (companyId: string) => {
  const cache = useContentSpaceRestCache()
  const [updating, setUpdating] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const update = useCallback(
    async (courseId: string, status: ContentSpacePublishStatus) => {
      setUpdating(true)
      setError(null)
      try {
        const result = await updateCourseStatus(companyId, courseId, status)
        cache.invalidate(`courses:${companyId}`)
        cache.invalidate(`course:${companyId}:${courseId}`)
        return result
      } catch (err) {
        setError(err as Error)
        throw err
      } finally {
        setUpdating(false)
      }
    },
    [cache, companyId],
  )

  return { updateCourseStatus: update, updating, error }
}

export { getPerCoursePortal }

// ========== COURSE VERSIONS HOOKS ==========

export const useCourseVersions = (companyId?: string, courseId?: string) => {
  const [data, setData] = useState<ICourseVersion[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const refetch = useCallback(() => {
    if (!companyId || !courseId) {
      return
    }

    setLoading(true)
    getCourseVersions(courseId, companyId)
      .then((data) => {
        setData(data)
        setError(null)
        setLoading(false)
      })
      .catch((error) => {
        const errorObj =
          error instanceof Error
            ? error
            : new Error(error?.message || error?.toString() || 'Unknown error')
        setError(errorObj)
        setLoading(false)
      })
  }, [companyId, courseId])

  useEffect(() => {
    refetch()
  }, [companyId, courseId, refetch])

  return { data, loading, error, refetch }
}

export const useActivateCourseVersion = (companyId: string, courseId: string) => {
  const [activating, setActivating] = useState<string | null>(null)

  const activate = useCallback(
    async (versionId: string) => {
      setActivating(versionId)
      try {
        await activateCourseVersion(courseId, versionId, companyId)
      } finally {
        setActivating(null)
      }
    },
    [companyId, courseId],
  )

  return { activate, activating }
}
